import { useEffect, useMemo, useState } from "react";
import { useDynamicRateContext } from "../../../context";
import { isStringEmpty } from "../../../utils/handler_validations";
import styles from "./styles";
import RatePreviewChart from "../../common/RatePreviewChart";
import {
  ColorPicker,
  GeofenceMap,
  InputSearch,
} from "@wallavi/very-geofencing-library";
import { VeryBtnFill } from "../../common/v2/VeryBtnFill";
import { classVeryBtnFill } from "../../common/v2/VeryBtnFill/classVeryBtnFill";
import { classVeryIconGoogle } from "../../common/v2/VeryIconGoogle/classVeryIconGoogle";
import { VeryIconGoogle } from "../../common/v2/VeryIconGoogle";
import { VerySwitch } from "../../common/v2/VerySwitch";
import CustomSelect from "../../common/custom-select";
import { VeryInput } from "../../common/v2/VeryInput";
import { classVeryInput } from "../../common/v2/VeryInput/classVeryInput";
import {
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from "@mui/material";

const propsTemplate = () => ({
  isLoaded: false,
});

const computeCentroid = (polygons) => {
  if (!Array.isArray(polygons) || polygons.length < 3) return null;
  const lat = polygons.reduce((s, p) => s + p.lat, 0) / polygons.length;
  const lng = polygons.reduce((s, p) => s + p.lng, 0) / polygons.length;
  return { lat, lng };
};

const ContainerRightRate = (props = propsTemplate()) => {
  const rates = useDynamicRateContext();
  const [confirmDynamicOpen, setConfirmDynamicOpen] = useState(false);

  const mapCenter = useMemo(() => {
    const centroid = computeCentroid(rates.data.polygons);
    return centroid ?? rates.data.ubicationCoordinates;
  }, [rates.data.polygons, rates.data.ubicationCoordinates]);

  const splitHour = (time) => {
    if (!time) return { hour: "", minute: "", period: "AM" };
    const regex = /^(\d{1,2}):(\d{2})\s?(AM|PM)$/i;
    const match = time.match(regex);

    if (!match) return { hour: "", minute: "", period: "AM" };

    return {
      hour: match[1],
      minute: match[2],
      period: match[3].toUpperCase(),
    };
  };
  const [startHourObj, setStartHourObj] = useState(
    () =>
      splitHour(rates.data.startHour) || { hour: "", minute: "", period: "AM" },
  );
  const [endHourObj, setEndHourObj] = useState(
    () =>
      splitHour(rates.data.endHour) || { hour: "", minute: "", period: "AM" },
  );

  const updateStartHour = (newPart) => {
    const updated = { ...startHourObj, ...newPart };
    setStartHourObj(updated);
    rates.updateData({
      startHour: `${updated.hour}:${updated.minute} ${updated.period}`,
    });
  };

  const updateEndHour = (newPart) => {
    const updated = { ...endHourObj, ...newPart };
    setEndHourObj(updated);
    rates.updateData({
      endHour: `${updated.hour}:${updated.minute} ${updated.period}`,
    });
  };

  return (
    <>
      {isStringEmpty(rates.data.id) ? null : (
        <styles.MainContainer>
          <styles.ContainerLeftEditRate>
            {/* HEADER */}
            <div className="divHeaderRight">
              <div className="divTitle">
                <div
                  className="divIcon"
                  onClick={() => rates.onChangeRowSelected(-1)}
                >
                  <VeryIconGoogle
                    name="chevron_left"
                    size={25}
                    color={classVeryIconGoogle.ICON_COLOR.BLACK_S1}
                    type={classVeryIconGoogle.ICON_TYPE.OUTLINED}
                  />
                </div>
                <VeryIconGoogle
                  name="map"
                  size={15}
                  color={classVeryIconGoogle.ICON_COLOR.BLACK_S1}
                  type={classVeryIconGoogle.ICON_TYPE.OUTLINED}
                />
                <span>Tarifa dinámica</span>
              </div>
              <div className="divRate">
                <div className="divRateTitle">
                  <VeryIconGoogle
                    name="paid"
                    type={classVeryIconGoogle.ICON_TYPE.FILLED}
                    size={25}
                    color={classVeryIconGoogle.ICON_COLOR.GRAY_S1}
                  />
                  <span>Tarifa General</span>
                  <Tooltip
                    className="tooltip"
                    title="Tarifa inicial general es el precio asignado por la aplicacion a nivel general"
                    placement="top"
                  >
                    <div>
                      <VeryIconGoogle
                        name="info"
                        type={classVeryIconGoogle.ICON_TYPE.OUTLINED}
                        size={20}
                        color={classVeryIconGoogle.ICON_COLOR.GRAY_S1}
                      />
                    </div>
                  </Tooltip>
                </div>
                <div className="divRateValue">
                  <span className="">$ 45.00 km</span>
                </div>
              </div>
            </div>
            {/* INPUTS */}
            <div className="divInputs">
              <div className="divInputText">
                <span>Nombre</span>
                <hr />
                <input
                  type="text"
                  value={rates.data.name}
                  onChange={(newName) =>
                    rates.updateData({ name: newName.target.value })
                  }
                />
              </div>
              <div className="divInputLocation">
                <div className="divLocationTitle">
                  <span>Ubicación</span>
                  <Tooltip
                    className="tooltip"
                    title="Ubicación es el área asignada para activar la tarifa dinámica"
                    placement="top"
                  >
                    <div>
                      <VeryIconGoogle
                        name="info"
                        type={classVeryIconGoogle.ICON_TYPE.OUTLINED}
                        size={20}
                        color={classVeryIconGoogle.ICON_COLOR.GRAY_S1}
                      />
                    </div>
                  </Tooltip>
                </div>
                <hr />
                <InputSearch
                  CSS={["inputLocation"]}
                  value={rates.data.ubicationName}
                  isLoaded={props.isLoaded}
                  onSelectLocation={(location) =>
                    rates.updateData({
                      ubicationName: location.name,
                      ubicationCoordinates: {
                        lat: parseFloat(location.lat),
                        lng: parseFloat(location.lng),
                      },
                    })
                  }
                />
              </div>
              <div className="divInputText">
                <div className="divInputTitle">
                  <span>Tarifa Inicial</span>
                  <Tooltip
                    className="tooltip"
                    title="Precio fijo al inicio del viaje y km cubiertos por ese precio. Si el viaje supera esos km y la tarifa dinámica está activa, se cobra extra."
                    placement="top"
                  >
                    <div>
                      <VeryIconGoogle
                        name="info"
                        type={classVeryIconGoogle.ICON_TYPE.OUTLINED}
                        size={20}
                        color={classVeryIconGoogle.ICON_COLOR.GRAY_S1}
                      />
                    </div>
                  </Tooltip>
                </div>
                <hr />
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <span style={{ fontSize: 12, color: "#8f8f8f" }}>$</span>
                    <input
                      type="text"
                      value={rates.data.initialRate}
                      style={{ width: 60 }}
                      onChange={(e) =>
                        rates.updateData({ initialRate: e.target.value })
                      }
                    />
                    <span style={{ fontSize: 11, color: "#8f8f8f" }}>MXN</span>
                  </div>
                  <span style={{ color: "#d1d5db", fontWeight: 300 }}>|</span>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <input
                      type="text"
                      value={rates.data.kilometers}
                      style={{ width: 42 }}
                      placeholder="1"
                      onChange={(e) =>
                        rates.updateData({ kilometers: e.target.value })
                      }
                    />
                    <span style={{ fontSize: 11, color: "#8f8f8f" }}>km</span>
                  </div>
                </div>
              </div>
            </div>
            {/* CONTENT MAP */}
            <div className="divMap">
              <div className="divHeaderMap">
                <span>Ubicación</span>
                {/* Color inline junto al botón */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginLeft: "auto",
                  }}
                >
                  <span
                    style={{ fontSize: 12, fontWeight: 700, color: "#444" }}
                  >
                    Color
                  </span>
                  <ColorPicker
                    valueColor={rates.data.color}
                    onChangeColor={(color) => rates.updateData({ color })}
                  />
                  <VeryBtnFill
                    colorTheme={classVeryBtnFill.COLOR_THEME.BLACK_S1}
                    label="Editar geocerca"
                    icon={{
                      name: "edit_location_alt",
                      size: 15,
                      type: classVeryIconGoogle.ICON_TYPE.OUTLINED,
                    }}
                    onClick={() => rates.showModalEditGeofence()}
                  />
                </div>
              </div>
              <GeofenceMap
                isLoaded={props.isLoaded}
                center={mapCenter}
                singlePolygon={
                  Array.isArray(rates.data.polygons) &&
                  rates.data.polygons.length > 0
                    ? { color: rates.data.color, polygons: rates.data.polygons }
                    : null
                }
                zoom={16}
                height="50vh"
                width="100%"
                mode="view"
                onPolygonUpdate={(polygons) =>
                  rates.updateData({ polygons: polygons })
                }
              />
            </div>
          </styles.ContainerLeftEditRate>
          <styles.ContainerRightEditRate>
            <VeryBtnFill
              colorTheme={classVeryBtnFill.COLOR_THEME.BLACK_S1}
              label="Guardar"
              onClick={() => rates.onClickBtnSave()}
            />
            {/* TARIFA DINAMICA */}
            <div className="divDynamicRate">
              <div className="divSwitchDynamic">
                <VerySwitch
                  selected={rates.data.isDynamic}
                  onChangeSelected={(selected) => {
                    if (!selected) {
                      setConfirmDynamicOpen(true);
                    } else {
                      rates.updateData({ isDynamic: true });
                    }
                  }}
                />
                <span>Tarifa Dinámica</span>
                <Tooltip
                  className="tooltip"
                  title="Tarifa Dinámica el costo aumenta de acuerdo a los parametros de distancia, horario y demanda "
                  placement="top"
                >
                  <div>
                    <VeryIconGoogle
                      name="info"
                      type={classVeryIconGoogle.ICON_TYPE.OUTLINED}
                      size={20}
                      color={classVeryIconGoogle.ICON_COLOR.GRAY_S1}
                    />
                  </div>
                </Tooltip>
              </div>
              <hr />
              <div className="divPricePerKilometerTitle">
                <span>Precio sobre kilómetros</span>
                <Tooltip
                  className="tooltip"
                  title="Tarifa de distancia se activa cuando se rebasan los limites permitidos de kilometros"
                  placement="top"
                >
                  <div>
                    <VeryIconGoogle
                      name="info"
                      type={classVeryIconGoogle.ICON_TYPE.OUTLINED}
                      size={20}
                      color={classVeryIconGoogle.ICON_COLOR.GRAY_S1}
                    />
                  </div>
                </Tooltip>
              </div>
              <div className="divPricePerKilometerInputs">
                <span>$</span>
                <input
                  type="text"
                  value={rates.data.pricePerKilometer}
                  onChange={(e) =>
                    rates.updateData({ pricePerKilometer: e.target.value })
                  }
                />
                <span style={{ color: "#8f8f8f", fontSize: 12 }}> / 1 km</span>
              </div>
              <hr />
              <div className="divScheduleTitle">
                <span>Horario</span>
                <Tooltip
                  className="tooltip"
                  title="Tarifa nocturna se activa apartir de las 10:00 PM hasta las 5:00 AM"
                  placement="top"
                >
                  <div>
                    <VeryIconGoogle
                      name="info"
                      type={classVeryIconGoogle.ICON_TYPE.OUTLINED}
                      size={20}
                      color={classVeryIconGoogle.ICON_COLOR.GRAY_S1}
                    />
                  </div>
                </Tooltip>
              </div>
              <div className="divScheduleInputs">
                <input
                  type="text"
                  value={startHourObj.hour}
                  onChange={(newHour) =>
                    updateStartHour({ hour: newHour.target.value })
                  }
                />
                <input
                  type="text"
                  value={startHourObj.minute}
                  onChange={(newMinute) =>
                    updateStartHour({ minute: newMinute.target.value })
                  }
                />
                <span> - </span>
                {/* <input
                  type="text"
                  value={startHourObj.period}
                  onChange={(newPeriod) =>
                    updateStartHour({ period: newPeriod.target.value })
                  }
                /> */}
                <select
                  value={startHourObj.period}
                  onChange={(newPeriod) =>
                    updateStartHour({ period: newPeriod.target.value })
                  }
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
              <div className="divScheduleInputs">
                <input
                  type="text"
                  value={endHourObj.hour}
                  onChange={(newHour) =>
                    updateEndHour({ hour: newHour.target.value })
                  }
                />
                <input
                  type="text"
                  value={endHourObj.minute}
                  onChange={(newMinute) =>
                    updateEndHour({ minute: newMinute.target.value })
                  }
                />
                <span> - </span>
                {/* <input
                  type="text"
                  value={endHourObj.period}
                  onChange={(newPeriod) =>
                    updateEndHour({ period: newPeriod.target.value })
                  }
                /> */}
                <select
                  value={endHourObj.period}
                  onChange={(newPeriod) =>
                    updateEndHour({ period: newPeriod.target.value })
                  }
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
              <div className="divPriceOnDemandTitle">
                <span>$</span>
                <input
                  type="text"
                  value={rates.data.priceOnDemand}
                  onChange={(newPriceOnDemand) =>
                    rates.updateData({
                      priceOnDemand: newPriceOnDemand.target.value,
                    })
                  }
                />
                <span>MXN</span>
              </div>
              <hr />
              <div className="divSwitchOnDemand">
                <VerySwitch
                  selected={rates.data.isDemand}
                  onChangeSelected={(selected) =>
                    rates.updateData({ isDemand: selected })
                  }
                />
                <span>Sobre demanda</span>
                <Tooltip
                  className="tooltip"
                  title="Tarifa sobre demanda se activa al contener demasiado trafico en la zona"
                  placement="top"
                >
                  <div>
                    <VeryIconGoogle
                      name="info"
                      type={classVeryIconGoogle.ICON_TYPE.OUTLINED}
                      size={20}
                      color={classVeryIconGoogle.ICON_COLOR.GRAY_S1}
                    />
                  </div>
                </Tooltip>
              </div>
            </div>
            {/* GRÁFICA — reemplaza sección Color */}
            <div className="divColor" style={{ gap: 8 }}>
              <RatePreviewChart
                initialRate={rates.data.initialRate}
                kilometers={rates.data.kilometers}
                pricePerKilometer={rates.data.pricePerKilometer}
                isDynamic={rates.data.isDynamic}
                priceOnDemand={
                  rates.data.isDemand ? rates.data.priceOnDemand : 0
                }
                width={240}
              />
            </div>
            {/* PRIORIDAD */}
            <div className="divPriority">
              <div className="divPriorityTitle">
                <span>Prioridad</span>
                <Tooltip
                  className="tooltip"
                  title="Cuando un viaje cae en varias geocercas, se aplica la de mayor prioridad. 1 = máxima, 3 = mínima."
                  placement="top"
                >
                  <div>
                    <VeryIconGoogle
                      name="info"
                      type={classVeryIconGoogle.ICON_TYPE.OUTLINED}
                      size={20}
                      color={classVeryIconGoogle.ICON_COLOR.GRAY_S1}
                    />
                  </div>
                </Tooltip>
              </div>
              <hr />
              <select
                value={rates.data.priority}
                onChange={(e) => rates.updateData({ priority: e.target.value })}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  borderRadius: 6,
                  border: "1px solid #d1d5db",
                  fontSize: 13,
                }}
              >
                <option value="principal">1 — Principal (máxima)</option>
                <option value="secundario">2 — Secundaria</option>
                <option value="terciario">3 — Terciaria (mínima)</option>
              </select>
            </div>
          </styles.ContainerRightEditRate>
        </styles.MainContainer>
      )}
      <Dialog
        open={confirmDynamicOpen}
        onClose={() => setConfirmDynamicOpen(false)}
      >
        <DialogTitle>¿Desactivar tarifa dinámica?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Al desactivar la tarifa dinámica,{" "}
            <strong>
              todos los viajes en esta geocerca cobrarán únicamente el monto
              base fijo
            </strong>{" "}
            (${rates.data.initialRate} MXN) sin importar la distancia recorrida.
            ¿Estás seguro?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDynamicOpen(false)}>Cancelar</Button>
          <Button
            color="error"
            onClick={() => {
              rates.updateData({ isDynamic: false });
              setConfirmDynamicOpen(false);
            }}
          >
            Sí, desactivar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ContainerRightRate;
